import { isLive, type Dataset, type Range, type TimeEntry } from "@otrack/domain";
import { projectRefs } from "./commands";
import { HOUR } from "./time";

export interface StrataRow {
  projectId: string | null;
  title: string;
  subtitle: string;
  entries: TimeEntry[];
}

export interface StrataAxis { from: number; to: number }

/**
 * One layer per Project worked on that day (in order of first use), then Projects with plans that
 * day, topped up with recently used Projects so there is always somewhere to drag new time onto.
 */
export function strataRows(dataset: Dataset, day: Range, minRows = 4, planned: ReadonlyArray<string | null> = []): StrataRow[] {
  const entries = Object.values(dataset.timeEntries)
    .filter((entry) => isLive(entry) && entry.start < day.to && (entry.end === null || entry.end > day.from))
    .sort((a, b) => a.start - b.start);
  const order: Array<string | null> = [];
  for (const entry of entries) if (!order.includes(entry.projectId)) order.push(entry.projectId);
  for (const projectId of planned) if (!order.includes(projectId)) order.push(projectId);
  for (const ref of projectRefs(dataset)) {
    if (order.length >= minRows) break;
    if (!order.includes(ref.id)) order.push(ref.id);
  }
  return order.map((projectId) => {
    const project = projectId ? dataset.projects[projectId] : undefined;
    const client = project?.clientId ? dataset.clients[project.clientId] : undefined;
    return {
      projectId,
      title: project ? client?.name ?? project.name : "No project",
      subtitle: project && client ? project.name : "",
      entries: entries.filter((entry) => entry.projectId === projectId)
    };
  });
}

/** Visible hours: 07:00 to 20:00, widened to fit the day's entries, its plans, and the current time. */
export function strataAxis(rows: readonly StrataRow[], day: Range, now: number, plans: ReadonlyArray<{ start: number; end: number }> = []): StrataAxis {
  let from = day.from + 7 * HOUR;
  let to = day.from + 20 * HOUR;
  for (const plan of plans) {
    from = Math.min(from, Math.max(day.from, plan.start));
    to = Math.max(to, Math.min(day.to, plan.end));
  }
  for (const row of rows) {
    for (const entry of row.entries) {
      from = Math.min(from, Math.max(day.from, entry.start));
      to = Math.max(to, Math.min(day.to, entry.end ?? now));
    }
  }
  if (now >= day.from && now < day.to) to = Math.max(to, now);
  const floorHour = (ts: number) => day.from + Math.floor((ts - day.from) / HOUR) * HOUR;
  const ceilHour = (ts: number) => day.from + Math.ceil((ts - day.from) / HOUR) * HOUR;
  return { from: floorHour(from), to: Math.min(day.to, ceilHour(to)) };
}

/** Position of a time on the axis, 0 to 1. */
export function axisFraction(axis: StrataAxis, ts: number): number {
  return Math.min(1, Math.max(0, (ts - axis.from) / (axis.to - axis.from)));
}
